import { MouseEvent, useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { UseMutationResult } from '@tanstack/react-query';

import { Button } from '@components/button';
import { SortableList } from '@components/drag-and-drop';
import { DraggableComponentProps } from '@components/drag-and-drop/sortable-list';
import { EmptyStateCell } from '@components/empty-state';
import { setContinueRegistry, useDeleteModal } from '@components/generic-modals';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { IconTraveller } from '@components/icons/traveller';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { ToggleGroup } from '@components/toggle-group';
import { useScopedTranslate } from '@locale/translate';
import { match } from '@utils/match';

import { AttributeType } from 'api/attributes/requests';
import {
  useCreateAttribute,
  useCreateJourney,
  useRemoveAttribute,
  useUpdateAttribute,
  useUpdateAttributeOrders,
} from 'api/journeys/hooks';
import {
  AttributeData,
  AttributeVisibility,
  JourneyAttributeData,
  visibilityMap,
} from 'api/journeys/requests';
import { routePaths } from 'ui/authenticated/navigation';
import { FieldCard } from 'ui/authenticated/settings/pages/attributes/field-card';
import { useFieldFormSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/field-form';

import { useCurrentWorkspace } from '../../helpers';

export const JourneyFields = ({
  attributes: journeyAttributes,
  journeyShortId,
}: {
  attributes: AttributeType[];
  journeyShortId: string;
}) => {
  const [attributes, setAttributes] = useState<Array<AttributeType>>(journeyAttributes);
  const { workspaceId } = useCurrentWorkspace();
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const createJourneyMutation = useCreateJourney();
  const updateAttributeMutation = useUpdateAttribute(journeyShortId);
  const updateAttributeOrdersMutation = useUpdateAttributeOrders(journeyShortId);
  const removeAttributeMutation = useRemoveAttribute(journeyShortId);
  const showAddAttributeSidebar = useFieldFormSidebar();
  const createAttributeMutation = useCreateAttribute();

  useEffect(() => {
    setAttributes(journeyAttributes);
  }, [journeyAttributes]);

  const addAttribute = async () => {
    if (journeyShortId === '') {
      const journeyData = await createJourneyMutation.mutateAsync({
        workspace: workspaceId ?? '',
        title: 'Untitled journey',
      });
      navigate(
        generatePath(routePaths.editJourney, {
          workspaceId: workspaceId,
          journeyId: journeyData.shortId,
          '*': 'attributes',
        }),
        {
          replace: true,
        }
      );

      // Allow the navigation to happen before launching
      requestAnimationFrame(() => {
        showAddAttributeSidebar({
          isTravellerAttribute: false,
          onCreate: (data: JourneyAttributeData) =>
            createAttributeMutation.mutateAsync({ journeyShortId: journeyData.shortId, data }),
        });
      });
    } else {
      showAddAttributeSidebar({
        isTravellerAttribute: false,
        onCreate: (data: JourneyAttributeData) =>
          createAttributeMutation.mutateAsync({ journeyShortId, data }),
      });
    }
  };
  const onAttributeUpdate = (updatedAttribute: AttributeType) => {
    setAttributes((prevAttributes) =>
      prevAttributes.map((attr) =>
        attr.shortId === updatedAttribute.shortId ? updatedAttribute : attr
      )
    );
  };
  const onAttributeRemove = (shortId: string) => {
    setAttributes((prevAttributes) => prevAttributes.filter((attr) => attr.shortId !== shortId));
  };

  const changeAttributeOrder = (activeIndex: number, overIndex: number) => {
    const newAttributes = [...attributes];
    const [removed] = newAttributes.splice(activeIndex, 1);
    newAttributes.splice(overIndex, 0, removed);
    setAttributes(newAttributes);
    updateAttributeOrdersMutation.mutate(
      newAttributes.reduce((acc, curr, idx) => ({ ...acc, [curr.shortId]: idx }), {})
    );
  };

  const addFieldButton = (
    <Button
      iconLeft={<IconAddPlus />}
      variant="secondary"
      onClick={addAttribute}
      text={t('addAttribute')}
    />
  );

  return (
    <PageContent>
      <MiddleColumnPage>
        <Stack space={4}>
          <Stack space={2}>
            <Text font="bold18" text={t('travellerSettings')} />
            <Text font="regular14" color="textSecondary" text={t('travellerSettingsDescription')} />
          </Stack>
          <Stack space={2}>
            {attributes.length === 0 && (
              <EmptyStateCell
                icon={<IconTraveller />}
                text={t('noAttributes')}
                action={addFieldButton}
              />
            )}
            <SortableList<AttributeComponentProps, AttributeItem>
              onChange={changeAttributeOrder}
              items={attributes}
              idProperty="shortId"
              DraggableComponent={Attribute}
              componentProps={{
                update: updateAttributeMutation,
                onAttributeUpdate,
                remove: removeAttributeMutation,
                onAttributeRemove,
              }}
            />
            {attributes.length > 0 && (
              <Stack orientation="horizontal" justify="flex-end">
                {addFieldButton}
              </Stack>
            )}
          </Stack>
        </Stack>
      </MiddleColumnPage>
    </PageContent>
  );
};

type AttributeItem = AttributeType;

type AttributeComponentProps = {
  update: UseMutationResult<
    any,
    unknown,
    {
      shortId: string;
      visibility?: AttributeVisibility;
      data?: AttributeData;
    },
    unknown
  >;
  remove: UseMutationResult<any, unknown, string, unknown>;
  onAttributeUpdate: (updatedAttribute: AttributeType) => void;
  onAttributeRemove: (shortId: string) => void;
};

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

const Attribute = ({
  item: attribute,
  attributes,
  listeners,
  update,
  remove,
  onAttributeUpdate,
  onAttributeRemove,
}: AttributeComponentProps & DraggableComponentProps<AttributeType>) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const showAttributeSidebar = useFieldFormSidebar();
  const isActive = useIsSidebarActive(attribute.shortId);

  const showFn = () => {
    showAttributeSidebar(
      {
        isTravellerAttribute: false,
        onEdit: (data: JourneyAttributeData) => {
          return update.mutateAsync({
            shortId: attribute.shortId,
            data: {
              title: data.title,
              options: data.options,
              attributeType: data.attributeType,
              ...(data.attributeType === 'number' || data.attributeType === 'decimal'
                ? { isDecimal: data.isDecimal }
                : {}),
            },
          });
        },
        attribute,
      },
      { id: attribute.shortId }
    );
  };

  // The sidebar's click-outside listener fires on mousedown, before this
  // Card's onClick — so we register the continue target on mousedown too,
  // otherwise the unsaved-changes modal pops with a stale target.
  const registerContinue = () => {
    setContinueRegistry({ id: attribute.shortId, onContinue: showFn });
  };

  const openEditSidebar = () => {
    registerContinue();
    showFn();
  };

  return (
    <FieldCard
      attributeType={attribute.attributeType}
      title={attribute.title}
      dragAttributes={attributes}
      dragListeners={listeners}
      isActive={isActive}
      onMouseDown={registerContinue}
      onClick={openEditSidebar}
      rightContent={
        <Stack orientation="horizontal" space={2} onClick={stopPropagation}>
          <ToggleGroup.Root
            value={
              !attribute.isActive && !attribute.isRequired
                ? 'disabled'
                : attribute.isActive && !attribute.isRequired
                ? 'optional'
                : 'required'
            }
            onChange={(value) => {
              match(value, {
                disabled: () => {
                  const updatedAttribute = {
                    ...attribute,
                    ...visibilityMap.disabled,
                  };
                  onAttributeUpdate(updatedAttribute);
                  update.mutate({
                    shortId: attribute.shortId,
                    visibility: 'disabled',
                  });
                },
                optional: () => {
                  const updatedAttribute = {
                    ...attribute,
                    ...visibilityMap.optional,
                  };
                  onAttributeUpdate(updatedAttribute);
                  update.mutate({
                    shortId: attribute.shortId,
                    visibility: 'optional',
                  });
                },
                required: () => {
                  const updatedAttribute = {
                    ...attribute,
                    ...visibilityMap.required,
                  };
                  onAttributeUpdate(updatedAttribute);
                  update.mutate({
                    shortId: attribute.shortId,
                    visibility: 'required',
                  });
                },
              });
            }}
          >
            <ToggleGroup.Button text={t('disabled')} value="disabled" variant="purple" />
            <ToggleGroup.Button text={t('optional')} value="optional" variant="orange" />
            <ToggleGroup.Button text={t('required')} value="required" variant="blue" />
          </ToggleGroup.Root>
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
            }}
          >
            <Menu.Item onSelect={openEditSidebar} label={t('editAttribute')} icon={<IconEdit />} />
            <Menu.Item
              onSelect={() => {
                return showDeleteModal({
                  title: t('core.delete'),
                  description: t('areYouSureRemove'),
                  hasUserConfirmation: true,
                  buttonText: t('core.delete'),
                  buttonIcon: <IconTrash />,
                  onConfirm: () => {
                    onAttributeRemove(attribute.shortId);
                    remove.mutate(attribute.shortId);
                  },
                });
              }}
              variant="danger"
              label={t('core.delete')}
              icon={<IconTrash />}
            />
          </Menu>
        </Stack>
      }
    />
  );
};

const locale = {
  en: {
    travellerSettings: 'Journey fields',
    travellerSettingsDescription:
      'Add and configure the fields the traveller should have for this journey',
    noAttributes: 'No fields added',
    disabled: 'Disabled',
    optional: 'Optional',
    required: 'Required',
    addAttribute: 'Create Journey field',
    editAttribute: 'Edit',
    areYouSureRemove:
      "Are you sure you want to delete this field from the journey? This change will affect all travellers regardless of their journey state ('Not started', 'In progress', 'Completed').",
  },
};
